import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetNonProfitById, useUpdateNonProfitOrgWiki } from '@/api/hooks/nonProfitOrgHooks';
import { useCreateManySalaries } from '@/api/hooks/salariesHooks';
import AddAnotherButton from '@/components/buttons/AddAnotherButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import SalaryForm from '@/components/forms/SalaryForm';
import FieldArrayListItemCard from '@/components/npo/onboarding/FieldArrayListItemCard';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { SalariesData, salariesResolver, SalaryData } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { objectComparision } from '@/utils/objectComparision';
import { useWikiChangesNpoModal } from '@/contexts/wikiChangesNpoModalContext';

const NpoSalaries = () => {
  const { initialList, salaryDataJson, salaryIndex, npoId } = useLocalSearchParams<{
    initialList: string;
    salaryDataJson: string;
    salaryIndex: string;
    npoId: string;
  }>();

  const [npoIdState, setNpoIdState] = useState(npoId);

  const { state: authState } = useAuth();
  const { data: npoData } = useGetNonProfitById({ id: npoIdState ?? authState.signUpData?.loginEmail?.npoId ?? '' });
  const { mutateAsync: createManySalaries, isPending: salariesPending } = useCreateManySalaries();
  const { mutateAsync: updateNpoWiki, isPending: updateNpoPending } = useUpdateNonProfitOrgWiki();

  const { showSuccess, showError } = useEndFlowModal();
  const { setVisible, setNpoId } = useWikiChangesNpoModal();

  const { height } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();

  useEffect(() => {
    if (npoId) setNpoIdState(npoId);
  }, [npoId]);

  useEffect(() => {
    if (salaryDataJson) {
      const salaryData = safeJsonParse<SalaryData>(salaryDataJson);
      if (salaryData && fields !== undefined) {
        // eslint-disable-next-line no-unused-expressions
        !salaryIndex && !fields.find((field) => objectComparision(field, salaryData))
          ? append(salaryData)
          : update(+salaryIndex, salaryData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaryDataJson]);

  const isIndividual = authState.authReady && authState.user?.user.role === 'INDIVIDUAL';
  const isChurch = authState.signUpData?.loginEmail?.type === 'CHURCH';

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    watch,
    setError,
    clearErrors,
    getFieldState,
    formState: { isValid, submitCount, errors }
  } = useForm<SalariesData>({
    defaultValues: { salaries: initialList !== undefined ? safeJsonParse<SalaryData[]>(initialList) : [] },
    mode: 'onSubmit',
    resolver: salariesResolver,
    context: { isIndividual, isChurch }
  });

  const { fields, insert, append, update, remove } = useFieldArray({
    control,
    name: 'salaries'
  });

  const salariesWatched = watch('salaries');
  const minimalSalaries = Math.min(Math.trunc((npoData?.totalEmployees ?? 0) / 10), 10);

  useEffect(() => {
    if (salariesWatched.length >= minimalSalaries) {
      clearErrors('salaries');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salariesWatched]);

  const onSubmit: SubmitHandler<SalariesData> = async (data) => {
    if (salariesWatched.length < minimalSalaries) {
      setError('salaries', {
        message: i18n.t('sign_up.nonProfit.wizard.errors.salaries.minimal', {
          minimal_salaries: minimalSalaries
        })
      });
    } else {
      const mappedData = data.salaries.map((salary) => ({
        ...salary,
        nonProfitOrgId: npoIdState ?? authState.signUpData?.loginEmail?.npoId ?? ''
      }));
      const body = {
        salaries: mappedData,
        npoId: npoIdState ?? authState.signUpData?.loginEmail?.npoId
      };

      try {
        if (isIndividual) {
          updateNpoWiki(
            {
              npoId: npoIdState ?? '',
              salaries: mappedData
            },
            {
              onSuccess: () => {
                setNpoId(npoIdState ?? '');
                setVisible(true);
              },
              onError: (e) => {
                showError({ message: e.message });
                console.error('Error', e);
              }
            }
          );
        } else {
          createManySalaries(body, {
            onSuccess: () => {
              showSuccess({
                title: i18n.t('end_flow.success_title'),
                message: i18n.t('npo_details.update_message')
              });
              router.back();
            },
            onError: (e) => {
              showError({ message: e.message });
              console.error('Error', e);
            }
          });
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <FormWrapper scrollEnable minHeightMinus={160}>
      {/* Height - Header */}
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.salaries.salaries_section')}
          description={i18n.t('sign_in.nonProfit.salaries.enter_at_least')}>
          {getValues('salaries') && getValues('salaries').length === 0 ? (
            // height - header - footer
            <View style={{ height: height - 160 - bottom - top }}>
              <SalaryForm
                initialData={getValues('salaries.0')}
                showDoneButton
                showBackButton={false}
                isLoading={salariesPending}
                manualValidation={isChurch}
                onDone={(data, triggerSalary) => {
                  if (!data.fullName && !data.position && !getFieldState('salaries').isDirty) {
                    if (isChurch) {
                      clearErrors();
                      handleSubmit(onSubmit)();
                    }
                    return;
                  }
                  if (data.fullName && data.position) {
                    clearErrors();
                    insert(0, data);
                    if (minimalSalaries <= 1) {
                      clearErrors('salaries');
                      handleSubmit(onSubmit)();
                    }
                  } else if (!data.fullName || !data.position || data.allowance || data.annualSalary) {
                    triggerSalary();
                  }
                }}
                onAdd={(data) => {
                  update(0, data);
                  trigger('salaries.0').then((isValid) => {
                    if (isValid) {
                      router.navigate({ pathname: '/edit/addUpdateSalary', params: { type: npoData?.type } });
                    }
                  });
                }}
                doneLabel={i18n.t('mix.done')}
                type={npoData?.type}
              />
            </View>
          ) : (
            <View style={styles.flexOne}>
              <Divider style={styles.divider} />
              <FlatList
                style={styles.flexOne}
                contentContainerStyle={styles.flexOne}
                scrollEnabled={false}
                data={fields}
                keyExtractor={(salary) => salary.id}
                renderItem={(field) => (
                  <FieldArrayListItemCard
                    onPress={() => {
                      router.navigate({
                        pathname: '/edit/addUpdateSalary',
                        params: {
                          salaryDataJson: JSON.stringify(field.item),
                          salaryIndex: field.index,
                          type: npoData?.type
                        }
                      });
                    }}
                    onDelete={() => {
                      remove(field.index);
                    }}
                    title={field.item.fullName}
                    subtitle={field.item.position}
                    budget={field.item.annualSalary}
                  />
                )}
                ListFooterComponent={
                  <AddAnotherButton
                    onPress={() =>
                      router.navigate({ pathname: '/edit/addUpdateSalary', params: { type: npoData?.type } })
                    }
                    label={i18n.t('sign_in.nonProfit.salaries.add_another')}
                  />
                }
              />
            </View>
          )}
        </OnboardingSection>

        <View style={styles.buttonsContainer}>
          {errors.salaries && <Text style={styles.errorText}>{errors?.salaries?.message}</Text>}

          {getValues('salaries') && getValues('salaries').length > 0 && (
            <BrandedButton
              loading={salariesPending || updateNpoPending}
              disabled={!isValid && submitCount > 0}
              onPress={handleSubmit(onSubmit)}>
              {i18n.t('mix.done')}
            </BrandedButton>
          )}
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  flexOne: {
    flex: 1
  },

  buttonsContainer: {
    flexDirection: 'column',
    width: '100%'
  },

  errorText: {
    color: 'red',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10
  },

  divider: {
    marginBottom: 15
  }
});

export default NpoSalaries;
